import { ArrowLeft20 } from '@carbon/icons-react';
import { Skeleton } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CpWidgetVariantType } from 'api/customer-portal/types';
import { CpSectionWrapper } from 'components/organisms/customer-portal/widgets/common';
import {
  CarouselWithControls,
  ControlButton,
  ControlsAtBottom,
  CpImageGalleryCarouselWrapper,
  RightArrow,
} from 'components/organisms/customer-portal/widgets/image-gallery/variants/image-gallery-carousel';
import { CpLoadingProps, TitleAndDesc } from '../common';

const RowCarouselImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--cp-spacing-06);
  margin-top: var(--cp-spacing-06);
`;

const RowCarouselImageThumbnailSkeleton = styled(Skeleton.Button)`
  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 333px !important;
    height: 249px !important;
  }
  margin-top: var(--cp-spacing-06);

  .ant-skeleton-button,
  &.ant-skeleton-button,
  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

const GridImageThumbnailSkeleton = styled(Skeleton.Button)`
  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 344px !important;
    height: 258px !important;
  }
  margin-top: var(--cp-spacing-06);

  .ant-skeleton-button,
  &.ant-skeleton-button,
  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

const GridImageGrid = styled.div`
  display: grid;
  gap: var(--cp-spacing-06);
  margin-top: var(--cp-spacing-06);
  /* no horizontal scroll */
  overflow-x: hidden;
  overflow-y: hidden;
  grid-template-columns: repeat(auto-fill, minmax(344px, 1fr));
`;

const BigImageSkeleton = styled(Skeleton.Button)`
  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 918px !important;
    height: 688px !important;
  }

  @media (max-width: 768px) {
    .ant-skeleton,
    &.ant-skeleton,
    & {
      max-width: 100% !important;
      width: 562px !important;
      height: 421px !important;
    }
  }

  @media (max-width: 480px) {
    .ant-skeleton,
    &.ant-skeleton,
    & {
      max-width: 100% !important;
      width: 328px !important;
      height: 246px !important;
    }
  }

  .ant-skeleton-button,
  &.ant-skeleton-button,
  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

const Content: React.FC<{ variantType: CpWidgetVariantType }> = ({
  variantType,
}) => {
  switch (variantType) {
    case CpWidgetVariantType.ImageGalleryRowCarousel:
      return (
        <RowCarouselImageGrid>
          <RowCarouselImageThumbnailSkeleton active />
          <RowCarouselImageThumbnailSkeleton active />
          <RowCarouselImageThumbnailSkeleton active />
        </RowCarouselImageGrid>
      );

    case CpWidgetVariantType.ImageGalleryGrid:
      return (
        <GridImageGrid>
          <GridImageThumbnailSkeleton active />
          <GridImageThumbnailSkeleton active />
          <GridImageThumbnailSkeleton active />
          <GridImageThumbnailSkeleton active />
          <GridImageThumbnailSkeleton active />
          <GridImageThumbnailSkeleton active />
        </GridImageGrid>
      );

    case CpWidgetVariantType.ImageGalleryCarousel:
      return (
        <CpImageGalleryCarouselWrapper areImagesAvailable={false}>
          <CarouselWithControls areImagesAvailable={false}>
            <ControlButton
              className="controlButton"
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
            />
            <Swiper
              pagination={{ clickable: true }}
              className="mySwiper"
              speed={500}
              spaceBetween={50}
              autoplay={{
                delay: 2000,
                disableOnInteraction: true,
              }}
            >
              <SwiperSlide>
                {/* <img src={img.attachment.location} alt={img.attachment.name} /> */}
                <BigImageSkeleton active />
              </SwiperSlide>
            </Swiper>
            <ControlButton
              className="controlButton"
              icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
            />
          </CarouselWithControls>
          <ControlsAtBottom>
            <ControlButton
              className="controlButtonMinimized"
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
            />
            <ControlButton
              className="controlButtonMinimized"
              icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
            />
          </ControlsAtBottom>
        </CpImageGalleryCarouselWrapper>
      );

    default:
      return <></>;
  }
};

export const CpImageGalleryLoading: React.FC<CpLoadingProps> = ({
  input,
  styles,
  variantType,
}) => {
  return (
    <CpSectionWrapper
      background={styles?.background as string}
      paddingY={styles?.paddingY as number | undefined}
      className="!cp-max-w-none"
    >
      <TitleAndDesc
        desc={input?.description as string}
        title={input?.title as string}
      />

      <Content variantType={variantType} />
    </CpSectionWrapper>
  );
};
